import React, { FC, useCallback, useMemo } from 'react';
import Slider, { Settings } from 'react-slick';
import { Box, Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { backgroundImageForUrl } from '@shared/utils/style/backgroundImage';
import { addRendition } from '@shared/utils/photoRendition';
import { useGetFilledPhotos } from '../../hooks/useGetFilledPhotos';
import { SliderContainer } from './PhotoSlider.styles';
import { Photo, PhotoSliderProps } from './PhotoSlider.types';
import { CroppedImage } from '@shared/components/MultiPhotoSelector/components/CroppedImage/CroppedImage';
import { useGetContainerDimensions } from '../../hooks/useGetContainerDimensions';

const PHOTO_COUNT_TO_SHOW = 6;
const ASPECT_RATIO = {
  desktop: '12/7',
  mobile: '1/1'
};

export const PhotoSlider: FC<PhotoSliderProps> = props => {
  const { photos, isMaxDimensionForCropping = false, containerAspectRatio, photoHeight } = props;

  const { filledPhotos } = useGetFilledPhotos(photos, PHOTO_COUNT_TO_SHOW, false);

  const { containerDimensions, containerRef } = useGetContainerDimensions();

  const settings: Settings = useMemo(
    () => ({
      dots: true,
      infinite: true,
      // animation speed
      speed: 2500,
      slidesToShow: 1,
      slidesToScroll: 1,
      fade: true,
      autoplay: true,
      // speed between slides on autoplay
      autoplaySpeed: 6000,
      cssEase: 'ease',
      pauseOnHover: false,
      // dot styling
      customPaging: () => <Box className="custom-dot" width={{ _: pxToRem(20), xs: 8, sm2: pxToRem(46) }} height={1} borderRadius="full" backgroundColor="white" />
    }),
    []
  );

  const getPhotoComponent = useCallback(
    (photo: Photo) => {
      if (photo.cropInfo) {
        return (
          <Box key={photo.id} width="100%" height="100%">
            <Flex
              width="100%"
              aspectRatio={containerAspectRatio}
              height={containerAspectRatio ? 'auto' : photoHeight || '100%'}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <CroppedImage
                width={isMaxDimensionForCropping ? Math.max(containerDimensions.width, containerDimensions.height) : containerDimensions.width}
                height={isMaxDimensionForCropping ? Math.max(containerDimensions.width, containerDimensions.height) : containerDimensions.height}
                key={photo.id}
                imageUrl={photo.photoUrl}
                cropInfo={{ ...photo.cropInfo, unit: '%' }}
                canvasStyles={{ position: 'absolute' }}
              />
            </Flex>
          </Box>
        );
      } else {
        return (
          <Box
            key={photo.id}
            width="100%"
            height={photoHeight || '100%'}
            aspectRatio={{ _: containerAspectRatio || ASPECT_RATIO.mobile, sm2: containerAspectRatio || ASPECT_RATIO.desktop }}
            backgroundImage={backgroundImageForUrl(addRendition({ url: photo.photoUrl, renditionSize: 'medium' }))}
            backgroundSize="cover"
            backgroundRepeat="no-repeat"
            backgroundPosition="center"
          />
        );
      }
    },
    [containerAspectRatio, containerDimensions.height, containerDimensions.width, isMaxDimensionForCropping, photoHeight]
  );

  return (
    <SliderContainer ref={containerRef} width="100%" height="100%" position="relative">
      <Slider {...settings}>
        {filledPhotos.map((photo, index) => {
          return photo.photoUrl ? (
            getPhotoComponent(photo)
          ) : (
            <Box
              key={index}
              width="100%"
              height={photoHeight || '100%'}
              aspectRatio={{ _: containerAspectRatio || ASPECT_RATIO.mobile, sm2: containerAspectRatio || ASPECT_RATIO.desktop }}
              backgroundColor="lightgray"
            />
          );
        })}
      </Slider>
    </SliderContainer>
  );
};

PhotoSlider.displayName = 'PhotoSlider';
